import { MouseEvent, useEffect, useState } from 'react';

import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { isNil } from 'ramda';
import { ChromePicker } from 'react-color';
import { useTranslation } from 'react-i18next';

import { TEXT } from 'constants/colors';

import {
  Box,
  Button,
  IconButton,
  Popover,
  styled,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { FontAwesomeSvgIcon } from '../';

type ColorSquareProps = {
  color: HexColor;
};
const ColorSquare = styled('span')<ColorSquareProps>((props) => ({
  width: '15px',
  height: '15px',
  border: '1px solid #dcdcdc',
  borderRadius: 2,
  background: props.color
    ? props.color
    : 'linear-gradient(to top left, #fff calc(50% - 1px), #dcdcdc , #fff calc(50% + 1px))'
}));

const SvgIcon = styled(FontAwesomeSvgIcon)`
  width: 17px;
  height: 17px;
`;

const StyledColorPicker = styled(ChromePicker)`
  box-shadow: none !important;
  font-family: 'signikalight' !important;
`;

export type HexColor = `#${string}` | '';

type Props = {
  onColorSelected: (color: HexColor) => void;
  color?: HexColor;
  label?: string;
  hasEditFeature?: boolean;
};

const ColorPickerPopover = ({
  color = '',
  label,
  onColorSelected,
  hasEditFeature
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<HexColor>(color);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const { t } = useTranslation();

  const isOpen = Boolean(anchorEl);
  const id = isOpen ? 'color-picker' : undefined;

  useEffect(() => {
    setValue(color);
  }, [color]);

  const onClickOpen = (evt: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(evt.currentTarget);
    setIsHovering(false);
  };

  const onClickSave = () => {
    onColorSelected(value);
    setAnchorEl(null);
    setIsHovering(false);
  };

  const onClickCancel = () => {
    setValue(color);
    setAnchorEl(null);
    setIsHovering(false);
  };

  const onChangeColor = ({ hex }) => {
    setValue(hex);
  };

  const onIconButtonMouseOver = () => {
    if (hasEditFeature) {
      setIsHovering(true);
    }
  };

  const onIconButtonMouseOut = () => {
    if (hasEditFeature) {
      setIsHovering(false);
    }
  };

  return (
    <Grid container direction="row">
      <Grid item>
        <IconButton
          onMouseOver={onIconButtonMouseOver}
          onMouseOut={onIconButtonMouseOut}
          onClick={onClickOpen}
          size="small"
          sx={{ marginLeft: label ? '-5px' : '' }}
        >
          {isHovering ? (
            <SvgIcon icon={faPencil} />
          ) : (
            <ColorSquare color={value} />
          )}
        </IconButton>
      </Grid>
      <Grid item sx={{ alignItem: 'center', padding: '5px' }}>
        {!isNil(label) && <Typography color={TEXT}>{label}</Typography>}
      </Grid>
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Box p={3} gap={2} display="flex" flexDirection="column">
          <StyledColorPicker
            disableAlpha={true}
            color={value}
            onChangeComplete={onChangeColor}
          />
          <Box display="flex" gap={2}>
            <Button variant="outlined" onClick={onClickCancel}>
              {t('cancel')}
            </Button>
            <Button variant="contained" onClick={onClickSave}>
              {t('save')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Grid>
  );
};

export default ColorPickerPopover;
